import { ageInYears, MIN_ACCOUNT_AGE } from '@mogzo/shared';
import { Pencil, Shuffle } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { type BirthMonth, BirthMonthField, toBirthMonth } from '../components/BirthMonthField';
import { Avatar } from '../components/community/Avatar';
import { UsernameField, type UsernameState } from '../components/UsernameField';
import { Alert, AuthLayout, Button, PageSpinner, TextLink } from '../components/ui';
import { ApiError, changeUsername, randomUsername, submitBirthMonth } from '../lib/api';
import { returnPath } from '../lib/auth-client';
import { usePublicConfig } from '../lib/public-config';
import { useSession } from '../lib/session';

const PROVIDER_NAMES: Record<string, string> = { google: 'Google', discord: 'Discord' };

const pillClass =
  'inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full border border-[#ead19a] bg-card px-4 text-sm font-bold text-ink hover:border-muted disabled:opacity-60';

/**
 * The first stop after a Google or Discord sign-up: the username we made up for them (they can
 * pick another or type their own) and their birth month, without which the account can't be
 * used.
 */
export function CompleteProfile() {
  const { me, refresh } = useSession();
  const config = usePublicConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const back = returnPath(params.get('next'));
  const [username, setUsername] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [usernameState, setUsernameState] = useState<UsernameState>('empty');
  const [shuffling, setShuffling] = useState(false);
  const [birth, setBirth] = useState<BirthMonth>({ month: '', year: '' });
  const [confirmUnder, setConfirmUnder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);

  if (removed) {
    return (
      <AuthLayout title="You can still play">
        <p className="text-ink">
          You need to be at least {MIN_ACCOUNT_AGE} to have a MOGZO account, so we didn’t keep one.
          You can still play games without an account.
        </p>
        <div className="mt-6">
          <TextLink to="/">Back to MOGZO</TextLink>
        </div>
      </AuthLayout>
    );
  }
  if (me === undefined) return <PageSpinner />;
  if (me === null) return <Navigate to="/sign-in" replace />;
  if (me.profileComplete) return <Navigate to={back ?? '/account'} replace />;

  const chosen = (username ?? me.username).trim();
  const providers = (me.signInMethods.length ? me.signInMethods : (config?.socialProviders ?? []))
    .map((method) => PROVIDER_NAMES[method])
    .filter(Boolean);

  async function pickAnother() {
    setShuffling(true);
    setError(null);
    try {
      setUsername((await randomUsername()).username);
      setTyping(false);
    } catch {
      setError('Couldn’t pick another just now. Try again.');
    } finally {
      setShuffling(false);
    }
  }

  async function save() {
    const birthMonth = toBirthMonth(birth);
    if (!birthMonth) return;
    setBusy(true);
    setError(null);
    try {
      if (chosen !== me?.username) await changeUsername(chosen);
      await submitBirthMonth(birthMonth);
      await refresh();
      navigate(back ?? '/account?welcome=1', { replace: true });
    } catch (failed) {
      if (failed instanceof ApiError && failed.code === 'age_restricted') {
        setRemoved(true);
        await refresh();
      } else {
        setError(failed instanceof Error ? failed.message : 'Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (typing && usernameState !== 'free' && usernameState !== 'yours') {
      return setError('Choose a username that’s free.');
    }
    const birthMonth = toBirthMonth(birth);
    if (!birthMonth) return setError('Choose your month and year of birth.');
    // The API deletes under-13 accounts, so make sure it isn't a slip of the year menu.
    if (ageInYears(birthMonth) < MIN_ACCOUNT_AGE && !confirmUnder) return setConfirmUnder(true);
    void save();
  }

  return (
    <AuthLayout title="Welcome to MOGZO" subtitle="Two quick things and you’re in.">
      <form onSubmit={onSubmit} className="space-y-5">
        {error && <Alert>{error}</Alert>}

        <section
          aria-labelledby="picked-heading"
          className="space-y-3 rounded-2xl border border-[#f3dfb5] bg-cream p-4"
        >
          <h2 id="picked-heading" className="text-sm font-semibold text-plum">
            {typing ? 'Your username' : 'We picked a username for you'}
          </h2>
          {typing ? (
            <UsernameField
              value={username ?? ''}
              onChange={setUsername}
              onState={setUsernameState}
              label="Type your own"
              focusOnShow
            />
          ) : (
            <p className="flex items-center gap-3">
              <Avatar name={chosen} />
              <span className="min-w-0 truncate text-[22px] font-bold tracking-[-0.01em]">
                {chosen}
              </span>
            </p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={pickAnother} disabled={shuffling} className={pillClass}>
              <Shuffle size={16} strokeWidth={2.2} aria-hidden="true" />
              Pick another
            </button>
            {!typing && (
              <button
                type="button"
                onClick={() => {
                  setUsername('');
                  setTyping(true);
                }}
                className={pillClass}
              >
                <Pencil size={16} strokeWidth={2} aria-hidden="true" />
                Type my own
              </button>
            )}
          </div>
          <p className="text-xs leading-relaxed text-plum">
            {providers.length > 0 &&
              `Your ${providers.join(' or ')} name and photo aren’t used anywhere on MOGZO. `}
            You can change your username later in your account.
          </p>
        </section>

        <div className="space-y-1.5">
          <BirthMonthField
            value={birth}
            onChange={(next) => {
              setBirth(next);
              setConfirmUnder(false);
            }}
          />
          <p className="text-xs text-muted">Only used to check your age. Nobody else sees it.</p>
        </div>
        {confirmUnder && (
          <Alert tone="info">
            That makes you under {MIN_ACCOUNT_AGE}. If it’s right, we’ll remove this account. Check
            the date, or continue to confirm.
          </Alert>
        )}
        <Button busy={busy}>{confirmUnder ? 'Confirm and continue' : 'Continue'}</Button>
      </form>
    </AuthLayout>
  );
}
